import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Link2OffIcon, Lightbulb, XIcon, ArrowRightIcon } from "lucide-react";
import { ProgressBar } from "@component/progress-bar";
import { SnackbarProvider } from "@hook/use-snackbar";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import {
    TrainingAnalyzingCard,
    type AnalyzingStatus,
} from "@component/training/training-analyzing-card";
import { catchErrorLog } from "@util/sentry";
import InterviewVoiceOnlyActive from "@container/interview-voice-only-active";
import {
    apiCanStartSession,
    apiGetSession,
    apiPollResults,
    getSessionHistory,
    retrySession,
} from "@service/training";
import type {
    TrainingCampaign,
    TrainingPersona,
    TrainingSession,
    SessionAttempt,
    RubricCriterion,
} from "@type/training";
import { TRAINING_TYPE_LABEL, DEFAULT_RUBRIC } from "@type/training";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import TrainingSetupPhase from "@component/training/training-setup-phase";

type Phase = "loading" | "brief" | "setup" | "live" | "analyzing" | "not_found";

const TRAINING_PRIVACY_LINE_1 =
    "ourteam uses generative AI to power your roleplay partner.";
const TRAINING_PRIVACY_LINE_2 =
    "Your roleplay is used only to coach you and is never used to train AI models.";
const TRAINING_TIP_TEXT =
    "Stay in character. Speak naturally and aim for back-and-forth turns.";

function parseDoHint(correctAnswer: string): string {
    return correctAnswer.split("\n")[0].trim().replace(/^[-•*]\s*/, "");
}

function parseDontHint(scoringGuide: string): string {
    const low = scoringGuide.split("\n").find((l) => /low score/i.test(l));
    return low ? low.replace(/^Low score\s*[—–-]\s*/i, "").trim() : "";
}

// Dev-only preview stubs. Accessed only inside the `import.meta.env.DEV`
// branch, so tree-shaken out of production builds entirely.
const PREVIEW_SESSION: TrainingSession = {
    id: "preview",
    campaign_id: "preview-campaign",
    employee_email: "sarah@acme.com",
    employee_name: "Sarah",
    token: "preview",
    attempt_number: 1,
    status: "invited",
    started_at: null,
    completed_at: null,
    rubric_scores: null,
    total_score: null,
    qualitative_feedback: null,
    is_test: true,
    session_duration: 5,
    session_expired_at: null,
};
const PREVIEW_CAMPAIGN: TrainingCampaign = {
    id: "preview-campaign",
    name: "Customer Service Excellence",
    training_type: "customer_service",
    persona_id: "preview-persona",
    status: "active",
    created_at: "2026-01-01T00:00:00.000Z",
    employee_count: 1,
    completed_count: 0,
    avg_score: null,
};
const PREVIEW_PERSONA: TrainingPersona = {
    id: "preview-persona",
    name: "Sarah Thompson",
    training_type: "customer_service",
    description: "An upset customer with a delayed order",
    briefing: "You are speaking with Sarah Thompson, a customer who placed an order 10 days ago and has not received it. She is frustrated and wants an immediate resolution — the item was a birthday gift and the birthday is tomorrow.",
};

export default function TrainingSessionPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [phase, setPhase] = useState<Phase>("loading");
    const [session, setSession] = useState<TrainingSession | null>(null);
    const [campaign, setCampaign] = useState<TrainingCampaign | null>(null);
    const [persona, setPersona] = useState<TrainingPersona | null>(null);
    const [analyzingStartedAt, setAnalyzingStartedAt] = useState<number>(0);
    const [analyzingStatus, setAnalyzingStatus] =
        useState<AnalyzingStatus>("running");
    const [analyzingFailedReason, setAnalyzingFailedReason] = useState<
        string | null
    >(null);
    const [attempts, setAttempts] = useState<SessionAttempt[]>([]);
    // Pre-flight gate state (BE handoff 2026-06-03). `checkingStart` debounces
    // the Begin button while can-start is in flight; `usedState` renders the
    // "already used" / "being scored" message when the gate returns 409.
    const [checkingStart, setCheckingStart] = useState(false);
    const [usedState, setUsedState] = useState<"completed" | "scoring" | null>(
        null,
    );
    const [rubricOpen, setRubricOpen] = useState(false);
    const [rubric, setRubric] = useState<RubricCriterion[]>(DEFAULT_RUBRIC);
    // Daily-free build: no callObject is created. The voice-only interview
    // container manages its own audio pipeline.

    // Resolve attempt history FIRST, then load the session. Based on the latest
    // attempt we may redirect before loading the current session:
    //   - latest attempt completed  → no active session, so retrySession() to
    //     mint a fresh attempt and go to its session URL.
    //   - latest attempt still active but a different token → go to it.
    // apiGetSession only runs once the history check decides we're staying on
    // this token, so we never flash the wrong brief.
    //
    // Prior attempts also power the state-dependent greeting + footer note copy
    // (first run / lapsed return / standard return). Best-effort: on a history
    // failure we fall back to the first-run copy and still load the session.
    useEffect(() => {
        if (!token) return;

        if (import.meta.env.DEV && token === "preview") {
            setSession(PREVIEW_SESSION);
            setCampaign(PREVIEW_CAMPAIGN);
            setPersona(PREVIEW_PERSONA);
            setRubric(DEFAULT_RUBRIC);
            setAttempts([]);
            setPhase("brief");
            return;
        }

        let cancelled = false;

        (async () => {
            let history: SessionAttempt[] = [];
            try {
                history = await getSessionHistory(token);
            } catch {
                history = [];
            }
            if (cancelled) return;
            setAttempts(history);

            if (history.length > 0) {
                const lastAttempt = history.reduce(
                    (latest, s) =>
                        s.attempt_number > latest.attempt_number ? s : latest,
                    history[0],
                );

                // A "used up" latest attempt is one the trainee can't resume:
                // either fully scored ("completed") or finished-and-still-being-
                // scored. The BE labels that scoring state "scoring" (per the BE
                // engineer); the earlier can-start handoff called the same DB
                // state "in_progress". We match all three so the proactive path
                // fires regardless of which string the attempt history returns.
                // A genuinely resumable mid-roleplay session is "started" and is
                // deliberately NOT in this set. For a used-up attempt we mint a
                // FRESH attempt and send the trainee to it; the old session keeps
                // its token and finishes scoring untouched. This is the proactive
                // path — the can-start gate in handleStart is the fallback if the
                // trainee somehow lands back on the old token directly.
                const lastAttemptUsedUp =
                    lastAttempt.status === "completed" ||
                    lastAttempt.status === "scoring" ||
                    lastAttempt.status === "in_progress";

                if (lastAttemptUsedUp) {
                    const newSession = await retrySession(token);
                    if (cancelled) return;
                    if (newSession?.token) {
                        navigate(`/training-session/${newSession.token}`, {
                            replace: true,
                        });
                    } else {
                        setPhase("not_found");
                    }
                    return;
                }

                // The latest attempt is still active (e.g. "started" — mid-roleplay)
                // — make sure the user is on its session URL (not an older attempt).
                // Stop here; the redirect remounts this page with the new token.
                if (lastAttempt.token && lastAttempt.token !== token) {
                    navigate(`/training-session/${lastAttempt.token}`, {
                        replace: true,
                    });
                    return;
                }
            }

            // No redirect — load the active session and show the brief.
            const result = await apiGetSession(token);
            if (cancelled) return;
            if (!result) {
                setPhase("not_found");
                return;
            }
            setSession(result.session);
            setCampaign(result.campaign);
            setPersona(result.persona);
            if (result.rubric_criteria?.length) {
                setRubric(result.rubric_criteria);
            }
            setPhase("brief");
        })();

        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleStart = async () => {
        if (!token) return;
        if (checkingStart) return; // debounce rapid double-clicks
        if (
            session?.session_expired_at &&
            new Date(session.session_expired_at).getTime() < Date.now()
        ) {
            return;
        }
        setCheckingStart(true);
        setUsedState(null);
        try {
            // Pre-flight gate: refuse to (re)start a session that's already been
            // used so a second click can't overwrite an attempt that is still
            // being scored. POST /start stays the authoritative gate, so on any
            // soft failure ("error") we let the user through.
            const canStart = await apiCanStartSession(token);
            if (canStart.kind === "used") {
                setUsedState(canStart.usedState);
                return;
            }
            if (canStart.kind === "not_found") {
                setPhase("not_found");
                return;
            }
            setPhase("live");
        } finally {
            setCheckingStart(false);
        }
    };

    // BE contract (post-2026-05-04 handoff): apiPollResults always returns a
    // PollResultsOutcome with kind "ready" | "scoring" | "failed". We poll on
    // "scoring" with exponential backoff, exit early on "ready" or "failed".
    type PollLoopResult =
        | { kind: "ready"; session: TrainingSession }
        | { kind: "failed"; reason: string }
        | { kind: "timeout" };

    const pollUntilReady = async (
        sessionToken: string,
        startedAt: number
    ): Promise<PollLoopResult> => {
        const TOTAL_BUDGET_MS = 90_000;
        const DELAYS = [1000, 2000, 4000, 8000, 8000, 8000, 8000, 8000, 8000];
        let attempt = 0;
        while (Date.now() - startedAt < TOTAL_BUDGET_MS) {
            try {
                const outcome = await apiPollResults(sessionToken);
                if (outcome.kind === "ready") {
                    return { kind: "ready", session: outcome.session };
                }
                if (outcome.kind === "failed") {
                    return { kind: "failed", reason: outcome.reason };
                }
            } catch (err) {
                catchErrorLog(
                    `[training] poll results threw: ${
                        err instanceof Error ? err.message : String(err)
                    }`
                );
            }
            const wait = DELAYS[Math.min(attempt, DELAYS.length - 1)];
            attempt += 1;
            await new Promise((r) => setTimeout(r, wait));
        }
        return { kind: "timeout" };
    };

    const handleEndSession = async () => {
        if (!token) return;
        const startedAt = Date.now();
        setAnalyzingStartedAt(startedAt);
        setAnalyzingStatus("running");
        setAnalyzingFailedReason(null);
        setPhase("analyzing");

        try {
            const result = await pollUntilReady(token, startedAt);
            if (result.kind === "ready") {
                navigate(`/training-results/${token}`, { replace: true });
                return;
            }
            if (result.kind === "failed") {
                setAnalyzingFailedReason(result.reason);
                setAnalyzingStatus("failed");
                return;
            }
            setAnalyzingStatus("timeout");
        } catch (err) {
            catchErrorLog(
                `[training] analyzing failed: ${
                    err instanceof Error ? err.message : String(err)
                }`
            );
            setAnalyzingStatus("failed");
        }
    };

    const handleAnalyzingRetry = async () => {
        if (!token) return;
        const startedAt = Date.now();
        setAnalyzingStartedAt(startedAt);
        setAnalyzingStatus("running");
        setAnalyzingFailedReason(null);
        const result = await pollUntilReady(token, startedAt);
        if (result.kind === "ready") {
            navigate(`/training-results/${token}`, { replace: true });
        } else if (result.kind === "failed") {
            setAnalyzingFailedReason(result.reason);
            setAnalyzingStatus("failed");
        } else {
            setAnalyzingStatus("timeout");
        }
    };

    if (phase === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-sm text-gray-500">Loading your session...</div>
            </div>
        );
    }

    if (phase === "not_found") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
                <Card className="max-w-md w-full">
                    <CardContent className="p-8 text-center">
                        <div className="text-lg font-semibold text-gray-900 mb-2">
                            Link not found or expired.
                        </div>
                        <div className="text-sm text-gray-600">
                            This training link is no longer valid. Please contact your HR team.
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!session || !campaign || !persona) return null;

    // A session whose expiry timestamp is in the past can no longer be started.
    // Source: session.session_expired_at (from apiGetSession).
    const isSessionExpired = session.session_expired_at
        ? new Date(session.session_expired_at).getTime() < Date.now()
        : false;

    // Mirror the "Link Expired" card used on the apply flow
    // (src/pages/apply-now/apply-v2.tsx). An expired link is terminal, so we
    // short-circuit the brief/live/analyzing phases entirely.
    if (isSessionExpired) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white p-6">
                <div className="w-full max-w-xl">
                    <div
                        className="bg-white rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                        style={{
                            boxShadow:
                                "0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)",
                        }}
                    >
                        <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                            <Link2OffIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                            Training Link Expired.
                        </h1>
                        <p className="text-gray-600 mx-auto whitespace-pre-line">
                            Your training session link has expired.
                            <br />
                            Please contact your admin for a new link.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // --- State-dependent brief copy (greeting + footer note) ---
    // Three states keyed off the attempt number and recency of prior runs:
    //   first run     → attempt_number === 1
    //   lapsed return → last completed run was 7+ days ago
    //   standard      → any other repeat attempt
    const name = session.employee_name;
    const attemptNumber = session.attempt_number;

    // Prior *completed & scored* attempts, excluding the current (unstarted) one.
    const priorCompleted = attempts
        .filter((a) => a.token !== token && a.completed_at && a.total_score !== null)
        .sort(
            (a, b) =>
                new Date(b.completed_at as string).getTime() -
                new Date(a.completed_at as string).getTime(),
        );
    const lastAttempt = priorCompleted[0];
    const lastScore = lastAttempt?.total_score ?? null;
    const bestScore = priorCompleted.length
        ? Math.max(...priorCompleted.map((a) => a.total_score ?? 0))
        : null;
    const daysSinceLastAttempt = lastAttempt?.completed_at
        ? Math.floor(
              (Date.now() - new Date(lastAttempt.completed_at).getTime()) / 86_400_000,
          )
        : null;

    const briefState =
        attemptNumber <= 1 || priorCompleted.length === 0
            ? "first"
            : daysSinceLastAttempt !== null && daysSinceLastAttempt >= 7
            ? "lapsed"
            : "standard";

    const greetingPrefix =
        briefState === "first"
            ? "Welcome,"
            : briefState === "lapsed"
            ? "Good to see you again,"
            : "Welcome back,";

    const resultsHref = lastAttempt ? `/training-results/${lastAttempt.token}` : null;

    if (phase === "live") {
        return (
            <SnackbarProvider>
                {session.is_test && (
                    <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-xs font-medium text-center py-2 px-4">
                        TEST MODE · This session won't count toward reports or KPIs.
                    </div>
                )}
                <div className="container mx-auto px-0 sm:px-0 pt-0 pb-2 sm:py-8 max-w-6xl h-full justify-center align-middle items-center sm:flex">
                    <div className="m-4 sm:m-0 w-full">
                            <SpeechDetectionProvider
                                volumeThreshold={30}
                                silenceDelay={400}
                                autoStart={true}
                            >
                            <InterviewVoiceOnlyActive onDeclined={() => setPhase("setup")} language={persona?.language ?? undefined} gender={persona?.gender ?? undefined} />
                        </SpeechDetectionProvider>
                    </div>
                </div>
            </SnackbarProvider>
        );
    }

    return (
        <SnackbarProvider>
        <div className="min-h-screen flex flex-col bg-white">
            {session.is_test && (
                <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-xs font-medium text-center py-2 px-4">
                    TEST MODE · This session won't count toward reports or KPIs.
                    {(phase === "brief" || phase === "setup") && (
                        <>
                            {" "}
                            <Link
                                to={`/training/campaigns/${session.campaign_id}`}
                                className="underline text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                Return to dashboard
                            </Link>
                        </>
                    )}
                </div>
            )}
            <div className="flex-1 flex items-center justify-center px-6 py-6 pb-24">
                <div className={`w-full ${phase === "setup" ? "max-w-6xl" : "max-w-[90rem]"}`}>

                    {phase === "brief" && (
                        <div className="flex items-center justify-center gap-10">
                            {/* Main content */}
                            <div className="w-full max-w-[54rem] shrink-0">
                                {/* Greeting header */}
                                <div className="mb-7">
                                    <div className="flex items-center gap-2 text-xs text-blue-600 uppercase tracking-wider font-semibold mb-3">
                                        <span>{TRAINING_TYPE_LABEL[campaign.training_type]} Roleplay</span>
                                        <span className="text-blue-400">·</span>
                                        <span>Attempt {attemptNumber}</span>
                                    </div>
                                    <h1 className="text-3xl sm:text-[2rem] font-bold text-gray-900 leading-snug">
                                        {greetingPrefix === "Welcome," ? "Hey" : greetingPrefix.replace(",", "")}{" "}
                                        {name}, welcome to your roleplay!
                                    </h1>
                                </div>

                                {/* Scenario */}
                                <div className="flex flex-col">
                                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-3">
                                        Your scenario
                                    </div>
                                    <div className="text-2xl font-semibold text-gray-900 mb-5">
                                        {persona.name}
                                    </div>
                                    <div className="mb-8">
                                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                                            Scenario Description
                                        </div>
                                        <div className="border-l-2 border-blue-400 pl-4">
                                            <p className="text-[0.95rem] text-gray-900 leading-relaxed">{persona.briefing}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 mb-8 text-center">
                                        <InfoTile
                                            label="Length"
                                            value={session.session_duration
                                                ? formatMinutes(session.session_duration)
                                                : "—"}
                                        />
                                        <InfoTile label="Format" value="Audio only" />
                                        <InfoTile
                                            label="Expires"
                                            value={session.session_expired_at
                                                ? formatExpiry(session.session_expired_at)
                                                : "—"}
                                        />
                                    </div>
                                    <div className="text-center text-sm text-gray-500 mb-6">
                                        {briefState === "first" ? (
                                            <>
                                                This is your first run.
                                                <br />
                                                Your score shows up on the Results page once you finish.
                                            </>
                                        ) : briefState === "lapsed" ? (
                                            <>
                                                It&apos;s been {daysSinceLastAttempt} day
                                                {daysSinceLastAttempt === 1 ? "" : "s"}.
                                                {bestScore !== null && (
                                                    <> Your best score so far is {bestScore}/100.</>
                                                )}{" "}
                                                Let&apos;s pick up where you left off.
                                            </>
                                        ) : (
                                            <>
                                                {lastScore !== null && (
                                                    <>Your last run was {lastScore}/100. See if you can beat it. </>
                                                )}
                                                All your scores are on the{" "}
                                                {resultsHref ? (
                                                    <Link
                                                        to={resultsHref}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        Results page
                                                    </Link>
                                                ) : (
                                                    "Results page"
                                                )}
                                                .
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {rubric.length > 0 && (
                                            <Button
                                                className="flex-1 py-[21px] rounded-full bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50"
                                                onClick={() => setRubricOpen((v) => !v)}
                                            >
                                                {rubricOpen ? "Hide scoring rubric" : "How will I be scored?"}
                                            </Button>
                                        )}
                                        <Button
                                            className="flex-1 px-8 py-[21px] rounded-full bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center justify-center gap-2"
                                            onClick={() => setPhase("setup")}
                                        >
                                            Proceed to Setup Check
                                            <ArrowRightIcon className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Inline rubric panel — slides in to the right of the main content */}
                            <div className={`transition-all duration-300 ease-in-out overflow-hidden shrink-0 ${
                                rubricOpen ? "w-[560px] opacity-100" : "w-0 opacity-0 pointer-events-none"
                            }`}>
                                <div className="w-[560px] border-l border-gray-100 px-8">
                                    <div className="flex items-center justify-between mb-5">
                                        <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                                            How you will be scored
                                        </span>
                                        <button
                                            onClick={() => setRubricOpen(false)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <XIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="space-y-4">
                                        {rubric.map((criterion) => (
                                            <div key={criterion.id}>
                                                <div className="flex items-baseline justify-between mb-1">
                                                    <span className="text-sm font-semibold text-gray-900">{criterion.name}</span>
                                                    <span className="text-xs font-medium text-gray-400 ml-3 shrink-0">{criterion.weight}%</span>
                                                </div>
                                                <p className="text-xs text-gray-500 leading-relaxed mb-2">{criterion.description}</p>
                                                {criterion.tip && (
                                                    <div className="flex gap-2 rounded-md bg-blue-50 px-3 py-2">
                                                        <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-blue-500" />
                                                        <p className="text-xs text-blue-800 leading-relaxed">{criterion.tip}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {phase === "setup" && (
                        <TrainingSetupPhase
                            persona={persona}
                            campaign={campaign}
                            userId={session.employee_email}
                            sessionToken={token!}
                            sessionDuration={session.session_duration}
                            onBack={() => setPhase("brief")}
                            onConfirm={handleStart}
                            confirmLoading={checkingStart}
                            usedState={usedState}
                        />
                    )}

                    {phase === "analyzing" && (
                        <TrainingAnalyzingCard
                            startedAt={analyzingStartedAt}
                            status={analyzingStatus}
                            failedReason={analyzingFailedReason}
                            onRetry={handleAnalyzingRetry}
                        />
                    )}
                </div>
            </div>
            {/* Fixed progress footer — shown on brief and setup phases */}
            {(phase === "brief" || phase === "setup") && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-8 py-5 z-40">
                    <ProgressBar
                        currentStep={phase === "brief" ? 1 : 2}
                        steps={[
                            {
                                label: "Roleplay Introduction",
                                isActive: phase === "brief",
                                isCompleted: phase === "setup",
                            },
                            {
                                label: "Setup Check",
                                isActive: phase === "setup",
                                isCompleted: false,
                            },
                        ]}
                    />
                </div>
            )}
        </div>
        </SnackbarProvider>
    );
}

function InfoTile({ label, value }: { label: string; value: string }) {
    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="text-base font-semibold text-gray-900 mt-1">{value}</div>
        </div>
    );
}

function formatMinutes(minutes: number): string {
    return `${minutes} min`;
}

function formatExpiry(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
